/**
 * ProductSelector
 * Dialog for selecting product lots for a Proforma Invoice.
 * Matches Transfer table UI: separate Cases / Units inputs per lot, budget-aware.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Button, Box, Typography, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, IconButton, useTheme
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { useProductSelectorData } from './product-selector-components/useProductSelectorData';
import ProductSelectorTable from './product-selector-components/ProductSelectorTable';

export default function ProductSelector({
  open,
  onClose,
  onItemsAdded,
  companyId,
  purchaseOrder,      // { po_number, price_list_id, customer_id }
  existingLineItems   // current line items in the PI
}) {
  const theme = useTheme();
  const { locations, groupedLots, committedCases, lotLoading, fetchLocations, fetchLotsForLocation } =
    useProductSelectorData(companyId);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  // lotSelections: { lotId: { cases: N, units: M } }
  const [lotSelections, setLotSelections] = useState({});
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  // IDs of lots already committed to this PI
  const existingInventoryItemIds = useMemo(() => {
    return new Set(
      (existingLineItems || [])
        .filter(i => i.inventory_item_id)
        .map(i => i.inventory_item_id)
    );
  }, [existingLineItems]);

  // Count lots with any selection
  const selectedCount = useMemo(
    () => Object.values(lotSelections).filter(v => (v.cases || 0) + (v.units || 0) > 0).length,
    [lotSelections]
  );

  // On open: load locations, reset state
  useEffect(() => {
    if (!open) return;
    fetchLocations();
    setLotSelections({});
    setItemSearchTerm('');
    setError('');
    setSelectedLocation('');
  }, [open, fetchLocations]);

  // Auto-select main location once locations load
  useEffect(() => {
    if (locations.length === 0 || selectedLocation) return;
    const main = locations.find(l => l.is_main) || locations[0];
    setSelectedLocation(main.id);
  }, [locations, selectedLocation]);

  // Reload lots when location changes
  useEffect(() => {
    if (selectedLocation) {
      fetchLotsForLocation(selectedLocation);
      setLotSelections({});
    }
  }, [selectedLocation, fetchLotsForLocation]);

  // Handle case or unit qty change for a lot
  const handleSelectionChange = useCallback((lotId, field, value) => {
    setLotSelections(prev => ({
      ...prev,
      [lotId]: {
        ...(prev[lotId] || { cases: 0, units: 0 }),
        [field]: value
      }
    }));
  }, []);

  const getPriceForVariant = async (productId, variantId, priceListId, unitType = 'Case') => {
    if (priceListId) {
      try {
        let q = supabase
          .from('price_list_items')
          .select('case_price, unit_price, price')
          .eq('price_list_id', priceListId)
          .eq('product_id', productId);
        if (variantId) q = q.eq('variant_id', variantId);
        else q = q.is('variant_id', null);
        const { data } = await q.maybeSingle();
        if (data) {
          if (unitType === 'Unit') return parseFloat(data.unit_price || data.price) || 0;
          return parseFloat(data.case_price || data.price) || 0;
        }
      } catch {
        // fall through
      }
    }
    return null;
  };

  const handleAddToOrder = async () => {
    setError('');

    // Collect all lots with any selection
    const lotsToAdd = [];
    for (const group of groupedLots) {
      for (const lot of group.lots) {
        if (existingInventoryItemIds.has(lot.id)) continue;
        const sel = lotSelections[lot.id] || {};
        const cases = sel.cases || 0;
        const units = sel.units || 0;
        if (cases > 0 || units > 0) {
          lotsToAdd.push({ group, lot, cases, units });
        }
      }
    }

    if (lotsToAdd.length === 0) {
      setError('Please enter a quantity for at least one lot.');
      return;
    }

    setAdding(true);
    try {
      const newLineItems = [];
      const priceListId = purchaseOrder?.price_list_id || null;

      for (const { group, lot, cases, units } of lotsToAdd) {
        const upc = lot.units_per_case || 1;
        const committedBaseUnits = committedCases[lot.id] || 0;
        const lotTotalCases = Math.floor((lot.total_base_units || 0) / upc);
        const lotTotalUnits = lot.total_base_units || 0;
        const freeCases = Math.max(0, lotTotalCases - Math.floor(committedBaseUnits / upc));
        const freeUnits = Math.max(0, lotTotalUnits - committedBaseUnits);

        const desc = group.variantName
          ? `${group.productName} - ${group.variantName}${lot.lot_number ? ` (Lot: ${lot.lot_number})` : ''}`
          : `${group.productName}${lot.lot_number ? ` (Lot: ${lot.lot_number})` : ''}`;

        const baseFields = {
          id: `new-${Date.now()}-${Math.random()}`,
          product_id: group.product_id,
          variant_id: group.variant_id || null,
          variant_name: group.variantName || '',
          product_name: group.productName,
          product_sku: group.sku || '',
          description: desc,
          units_per_case: upc,
          location_id: lot.location_id,
          inventory_item_id: lot.id,
          lot_number: lot.lot_number || null,
          batch_number: lot.batch_number || null,
          manufactured_date: lot.manufactured_date || null,
          expiry_date: lot.expiry_date || null,
          line_status: 'active'
        };

        if (cases > 0) {
          let price = await getPriceForVariant(group.product_id, group.variant_id, priceListId, 'Case');
          if (price === null) price = group.casePrice || 0;
          newLineItems.push({
            ...baseFields,
            id: `new-${Date.now()}-${Math.random()}-c`,
            quantity: cases,
            unit_type: 'Case',
            unit_price: price,
            line_total: cases * price,
            available_cases: freeCases
          });
        }

        if (units > 0) {
          let price = await getPriceForVariant(group.product_id, group.variant_id, priceListId, 'Unit');
          if (price === null) price = group.unitPrice || 0;
          newLineItems.push({
            ...baseFields,
            id: `new-${Date.now()}-${Math.random()}-u`,
            quantity: units,
            unit_type: 'Unit',
            unit_price: price,
            line_total: units * price,
            available_cases: freeUnits
          });
        }
      }

      onItemsAdded(newLineItems);
      onClose();
    } catch (err) {
      console.error('Error adding items to order:', err);
      setError('Failed to add items: ' + err.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={adding ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: '70vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1.5
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Select Products for Proforma Invoice
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={adding}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search products..."
            value={itemSearchTerm}
            onChange={(e) => setItemSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              )
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
          />

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={selectedLocation}
              label="Location"
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map(loc => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!selectedLocation ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, color: 'text.secondary' }}>
            <Typography variant="body2">Select a location to view available inventory.</Typography>
          </Box>
        ) : lotLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductSelectorTable
            groupedLots={groupedLots}
            committedCases={committedCases}
            lotSelections={lotSelections}
            onSelectionChange={handleSelectionChange}
            existingInventoryItemIds={existingInventoryItemIds}
            itemSearchTerm={itemSearchTerm}
          />
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {selectedCount > 0 && (
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
              {selectedCount} lot{selectedCount !== 1 ? 's' : ''} selected
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button onClick={onClose} disabled={adding} sx={{ borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToOrder}
            disabled={adding || selectedCount === 0}
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {adding ? 'Adding...' : `Add to Order${selectedCount > 0 ? ` (${selectedCount} lot${selectedCount !== 1 ? 's' : ''})` : ''}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
